import React from 'react';
import './PhoneInput.css';

/**
 * PhoneInput — single-row input with a visual `+91 🇮🇳` prefix and a
 * 10-digit-only entry field for Indian mobile numbers.
 *
 * Why this component exists:
 *   The previous design was a plain `<input>` with placeholder "9876543210"
 *   and a separate caption "no country code". Half of users still typed
 *   `+91 9876543210` or `09876543210` and hit a regex error. Showing the
 *   prefix as part of the field removes that whole class of mistakes.
 *
 * Props:
 *   value         — the 10-digit string (no country code), e.g. "9876543210"
 *   onChange      — called with the cleaned 10-digit string
 *   placeholder   — default "9876543210"
 *   required, autoFocus, disabled — passed straight to the input
 *
 * The component never lets the user type more than 10 digits, and it
 * strips anything that isn't a digit. Always returns a clean 10-digit
 * string up to the parent.
 */
function PhoneInput({
  value,
  onChange,
  placeholder = '9876543210',
  required = false,
  autoFocus = false,
  disabled = false,
  id,
  name = 'phone',
  autoComplete = 'tel-national',
}) {
  const handleChange = (e) => {
    // Keep only digits, trim to 10. Pasting "+91 98765 43210" cleans to "9876543210".
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
    onChange(cleaned);
  };

  return (
    <div className="phone-input">
      <span className="phone-input__prefix" aria-hidden="true">
        +91 <span role="img" aria-label="India">🇮🇳</span>
      </span>
      <input
        id={id}
        name={name}
        type="tel"
        inputMode="numeric"
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        required={required}
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={10}
        className="phone-input__field"
      />
    </div>
  );
}

export default PhoneInput;
